'use client';

/**
 * Arena Leaderboard tab (E2/E3 basics). Consumes the SIBLING-owned
 * GET /api/arena/experiments/[id]/leaderboard endpoint. Until that endpoint
 * exists it 404s — this renders a graceful "not available yet" state on ANY
 * error, never crashing, so the review UI is testable independently.
 *
 * Sortable table (default elo desc) + a dependency-free SVG cost-vs-elo scatter.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartScatter, Trophy } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { formatUsd, formatLatency } from './constants';

export interface LeaderboardVariant {
  variantId: string;
  label: string;
  model: string | null;
  provider: string | null;
  generationCount: number;
  succeededCount: number;
  failedCount: number;
  avgCostUsd: number | null;
  avgLatencyMs: number | null;
  elo: number | null;
  pairwiseWins: number;
  pairwiseLosses: number;
  pickCount: number;
  rubricMeans: Record<string, number> | null;
  tagCounts: Record<string, number> | null;
  judgeAcceptRate: number | null;
  judgeMeanScore: number | null;
}

export interface LeaderboardData {
  experimentId: string;
  computedAt: string;
  variants: LeaderboardVariant[];
}

type SortKey = 'elo' | 'pairwiseWins' | 'pickCount' | 'avgCostUsd' | 'avgLatencyMs' | 'judgeAcceptRate';

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'elo', label: 'Elo' },
  { key: 'pairwiseWins', label: 'W' },
  { key: 'pickCount', label: 'Picks' },
  { key: 'avgCostUsd', label: 'Avg cost' },
  { key: 'avgLatencyMs', label: 'Avg latency' },
  { key: 'judgeAcceptRate', label: 'Judge accept' },
];

function num(v: number | null | undefined): number {
  return typeof v === 'number' ? v : -Infinity;
}

export function LeaderboardTable({ data }: { data: LeaderboardData }) {
  const [sortKey, setSortKey] = useState<SortKey>('elo');
  const [asc, setAsc] = useState(false);

  const rows = useMemo(() => {
    const sorted = [...data.variants].sort((a, b) => num(a[sortKey]) - num(b[sortKey]));
    return asc ? sorted : sorted.reverse();
  }, [data.variants, sortKey, asc]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) setAsc((a) => !a);
    else {
      setSortKey(key);
      setAsc(false);
    }
  };

  if (data.variants.length === 0) {
    return (
      <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-border/60 bg-card/40 text-center">
        <Trophy weight="duotone" className="size-8 text-muted-foreground" />
        <p className="mt-3 text-sm font-semibold">No leaderboard data yet</p>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground">
          Once generations succeed and reviews land, rankings appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="arena-leaderboard-table">
      <div className="overflow-x-auto rounded-2xl border border-border/60">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Variant</th>
              <th className="px-3 py-2 font-semibold">Model</th>
              {COLUMNS.map((c) => (
                <th key={c.key} className="px-3 py-2 font-semibold">
                  <button
                    type="button"
                    onClick={() => onSort(c.key)}
                    className={cn(
                      'inline-flex items-center gap-1 hover:text-foreground',
                      sortKey === c.key && 'text-foreground',
                    )}
                  >
                    {c.label}
                    {sortKey === c.key && <span>{asc ? '▲' : '▼'}</span>}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((v, i) => (
              <tr key={v.variantId} className="border-b border-border/40 last:border-0">
                <td className="px-3 py-2 font-semibold">
                  <span className="mr-1.5 text-muted-foreground">#{i + 1}</span>
                  {v.label}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {v.model ?? '—'}
                  {v.provider ? <span className="opacity-60"> · {v.provider}</span> : null}
                </td>
                <td className="px-3 py-2 tabular-nums">{v.elo != null ? Math.round(v.elo) : '—'}</td>
                <td className="px-3 py-2 tabular-nums">
                  {v.pairwiseWins}
                  <span className="text-muted-foreground">/{v.pairwiseLosses}</span>
                </td>
                <td className="px-3 py-2 tabular-nums">{v.pickCount}</td>
                <td className="px-3 py-2 tabular-nums">{formatUsd(v.avgCostUsd)}</td>
                <td className="px-3 py-2 tabular-nums">{formatLatency(v.avgLatencyMs)}</td>
                <td className="px-3 py-2 tabular-nums">
                  {v.judgeAcceptRate != null ? `${Math.round(v.judgeAcceptRate * 100)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CostEloScatter variants={data.variants} />
    </div>
  );
}

function CostEloScatter({ variants }: { variants: LeaderboardVariant[] }) {
  const pts = variants.filter((v) => v.elo != null && v.avgCostUsd != null) as (LeaderboardVariant & {
    elo: number;
    avgCostUsd: number;
  })[];
  if (pts.length < 2) return null;

  const W = 520;
  const H = 220;
  const pad = 36;
  const costs = pts.map((p) => p.avgCostUsd);
  const elos = pts.map((p) => p.elo);
  const minC = Math.min(...costs);
  const maxC = Math.max(...costs);
  const minE = Math.min(...elos);
  const maxE = Math.max(...elos);
  const x = (c: number) => pad + ((c - minC) / (maxC - minC || 1)) * (W - pad * 2);
  const y = (e: number) => H - pad - ((e - minE) / (maxE - minE || 1)) * (H - pad * 2);

  return (
    <div className="rounded-2xl border border-border/60 bg-card/40 p-4">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <ChartScatter weight="duotone" className="size-4" /> Cost vs Elo frontier
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Cost versus Elo scatter">
        <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} className="stroke-border" strokeWidth={1} />
        <line x1={pad} y1={pad} x2={pad} y2={H - pad} className="stroke-border" strokeWidth={1} />
        <text x={W / 2} y={H - 6} textAnchor="middle" className="fill-muted-foreground text-[10px]">
          avg cost (usd) →
        </text>
        <text x={12} y={H / 2} textAnchor="middle" transform={`rotate(-90 12 ${H / 2})`} className="fill-muted-foreground text-[10px]">
          elo →
        </text>
        {pts.map((p) => (
          <g key={p.variantId}>
            <circle cx={x(p.avgCostUsd)} cy={y(p.elo)} r={5} className="fill-violet-400" />
            <text x={x(p.avgCostUsd) + 7} y={y(p.elo) + 3} className="fill-foreground text-[10px] font-semibold">
              {p.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export function LeaderboardTab({ experimentId }: { experimentId: string }) {
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(async () => {
    setState('loading');
    try {
      const res = await fetch(`/api/arena/experiments/${experimentId}/leaderboard`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const json = (await res.json()) as LeaderboardData;
      setData(json);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [experimentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state === 'loading') {
    return <div className="min-h-[220px] animate-pulse rounded-2xl border border-border/60 bg-card/40" />;
  }
  if (state === 'error' || !data) {
    return (
      <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-border/60 bg-card/40 text-center" data-testid="arena-leaderboard-empty">
        <ChartScatter weight="duotone" className="size-8 text-muted-foreground" />
        <p className="mt-3 text-sm font-semibold">Leaderboard not available yet</p>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground">
          The judge + leaderboard service is still coming online. Objective metrics
          (cost, latency) and your reviews are already being recorded.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 rounded-full border border-border/60 px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          Retry
        </button>
      </div>
    );
  }
  return <LeaderboardTable data={data} />;
}
